import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, X } from 'lucide-react';

/**
 * ConfirmModal - Modal di conferma elegante per azioni destructive
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Se il modal è aperto
 * @param {Function} props.onClose - Callback per chiudere il modal
 * @param {Function} props.onConfirm - Callback per confermare l'azione
 * @param {string} props.title - Titolo del modal
 * @param {string} props.message - Messaggio di conferma
 * @param {string} props.confirmText - Testo bottone conferma (default: "Conferma")
 * @param {string} props.cancelText - Testo bottone annulla (default: "Annulla")
 * @param {string} props.variant - Variante: 'danger', 'warning', 'info' (default: 'danger')
 * @param {boolean} props.loading - Stato loading per il bottone conferma
 * @param {string} props.zIndexClass - Classe z-index (default: 'z-50'; più alta se aperto sopra un modal a schermo intero)
 */
const ConfirmModal = ({
    isOpen,
    onClose,
    onConfirm,
    title = "Sei sicuro?",
    message = "Questa azione non può essere annullata",
    confirmText = "Conferma",
    cancelText = "Annulla",
    variant = "danger",
    loading = false,
    zIndexClass = 'z-50'
}) => {
    const variants = {
        danger: {
            icon: <AlertTriangle className="text-red-600" size={48} />,
            iconBg: 'bg-red-100 dark:bg-red-900/30',
            confirmBtn: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
        },
        warning: {
            icon: <AlertTriangle className="text-orange-600" size={48} />,
            iconBg: 'bg-orange-100 dark:bg-orange-900/30',
            confirmBtn: 'bg-orange-600 hover:bg-orange-700 focus:ring-orange-500',
        },
        info: {
            icon: <AlertTriangle className="text-blue-600" size={48} />,
            iconBg: 'bg-blue-100 dark:bg-blue-900/30',
            confirmBtn: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500',
        }
    };

    const currentVariant = variants[variant] || variants.danger;

    const handleConfirm = async () => {
        if (loading) return;
        await onConfirm();
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className={`fixed inset-0 bg-black/50 backdrop-blur-sm ${zIndexClass}`}
                    />

                    {/* Modal */}
                    <div className={`fixed inset-0 flex items-center justify-center p-4 ${zIndexClass}`}>
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.9, opacity: 0, y: 20 }}
                            transition={{ type: "spring", duration: 0.5 }}
                            className="relative flex max-w-md w-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-white/10 dark:bg-gray-900 dark:shadow-black/35"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Close button */}
                            <button
                                onClick={onClose}
                                className="absolute top-3 right-3 z-10 rounded-lg p-2 transition-colors hover:bg-black/5 dark:hover:bg-white/10"
                            >
                                <X size={20} className="text-gray-500 dark:text-white/80" />
                            </button>

                            {/* Header */}
                            <div className="flex min-h-14 shrink-0 items-center border-b border-gray-200 bg-[#dad8e5] px-5 pr-14 dark:border-white/10 dark:bg-gray-800">
                                <h3 className="text-lg font-bold text-gray-900 dark:font-open-sans dark:text-white">
                                    {title}
                                </h3>
                            </div>

                            <div className="flex-1 bg-white px-6 py-6 dark:bg-gray-900">
                                {/* Icon */}
                                <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ delay: 0.2, type: "spring" }}
                                    className={`
                                        mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full
                                        ${currentVariant.iconBg}
                                    `}
                                >
                                    {currentVariant.icon}
                                </motion.div>

                                {/* Message */}
                                <p className="mb-6 text-center text-gray-600 dark:text-gray-300">
                                    {message}
                                </p>

                                {/* Actions */}
                                <div className="flex gap-3">
                                <button
                                    onClick={onClose}
                                    disabled={loading}
                                    className="
                                        flex-1 rounded-xl px-4 py-3 font-medium transition-colors
                                        bg-gray-100 text-gray-900 hover:bg-gray-200
                                        dark:bg-gray-800 dark:font-open-sans dark:text-white dark:hover:bg-indigo-700
                                        disabled:cursor-not-allowed disabled:opacity-50
                                    "
                                >
                                    {cancelText}
                                </button>
                                <button
                                    onClick={handleConfirm}
                                    disabled={loading}
                                    className={`
                                        flex-1 px-4 py-3 rounded-xl font-medium
                                        text-white transition-all
                                        focus:outline-none focus:ring-4 focus:ring-opacity-50
                                        disabled:opacity-50 disabled:cursor-not-allowed
                                        ${currentVariant.confirmBtn}
                                        ${loading ? 'cursor-wait' : ''}
                                    `}
                                >
                                    {loading ? (
                                        <span className="flex items-center justify-center gap-2">
                                            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Caricamento...
                                        </span>
                                    ) : confirmText}
                                </button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </>
            )}
        </AnimatePresence>
    );
};

export default ConfirmModal;

